import { IsoDuration } from '../../types/iso-date';

/**
 * Validate an ISO 8601 duration string.
 *
 * Supported forms (uppercase only):
 * - Sign optional: '+' | '-' prefix allowed.
 * - Weeks-only: PnW (n may be integer or decimal with up to 6 fractional digits).
 * - Date/Time: P[nY][nM][nD][T[nH][nM][nS]] with n integer or decimal (up to 6 fractional digits).
 *
 * Rules:
 * - At least one duration component must be present.
 * - Weeks form (PnW) cannot be combined with any other component.
 * - If 'T' is present, at least one of H/M/S must follow.
 * - Only '.' as decimal separator is allowed; up to 6 fractional digits (to match project precision).
 */
export function isIsoDuration(str: string): str is IsoDuration {
  if (!str) return false;

  // General date/time duration:
  // [+-]?P [YMD parts] [T [HMS parts]]
  // Each numeric token may be integer or decimal (1-6 fractional digits)
  const num = '(?:\\d+(?:\\.\\d{1,6})?)';
  const y = `(?:${num}Y)`;
  const mo = `(?:${num}M)`; // month (in date part)
  const w = `(?:${num}W)`; // week
  const d = `(?:${num}D)`;
  const h = `(?:${num}H)`;
  const mi = `(?:${num}M)`; // minute (in time part)
  const s = `(?:${num}S)`;

  // Date part: any of Y, M, D in that order; but individually optional
  // Time part: T followed by any of H, M, S in that order; but at least one present if T is there
  const datePart = `(?:${y})?(?:${mo})?(?:${w})?(?:${d})?`;
  const timePart = `T(?:(?:${h})?(?:${mi})?(?:${s})?)`;
  const pattern = new RegExp(
    `^[+-]?P(?:(?:${datePart})(?:${timePart})?|${datePart})$`
  );

  if (!pattern.test(str)) return false;

  // Additional semantic checks:
  // - Ensure at least one component present (not just 'P' or '+P'/'-P')
  // - If 'T' is present, ensure at least one of H/M/S exists
  // - Ensure no weeks combined (already ensured by separate regex)

  // Must have something other than the sign and 'P' and maybe 'T'
  const core = str.replace(/^[+-]?P/, '');
  if (!core) return false; // just 'P'

  // If it's just 'T' or empty after removing date components -> invalid
  if (core === 'T') return false;

  // If 'T' present, verify at least one H/M/S exists
  if (core.includes('T')) {
    const time = core.slice(core.indexOf('T') + 1);
    if (!/(?:\d|\.)[HMS]/.test(time)) return false;
  }

  return true;
}
